import { Link, Outlet, createRoute, redirect, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { rootRoute } from '../root.js';
import {
  isPlatformAuthenticated,
  platformLogout,
  refreshPlatformAccessToken,
} from '../../lib/platform-auth.js';

/**
 * Platform operator console shell — a layout route (id, no path of its own).
 * Every authenticated /platform/* page nests under this.
 *
 * Guard (beforeLoad): if there's no in-memory super-admin token, attempt one
 * silent refresh against /v1/platform/auth/refresh (the __sa_refresh cookie).
 * If that fails, bounce to /platform/login. This is the platform analogue of
 * the tenant authedLayoutRoute guard, but it folds the silent-refresh in here
 * (the tenant app does it in AuthContext on mount; the platform side has no
 * provider, so the guard owns it).
 *
 * Visual differentiation (locked decision): Indigo-Deep (#16153A) sidebar +
 * "Platform Operator Console" header, so an operator can tell within a second
 * that this is the platform surface, not the tenant admin app.
 */
/**
 * The guard, extracted so it can be unit-tested directly: no in-memory token →
 * attempt one silent refresh → still nothing → redirect to /platform/login.
 */
export async function platformConsoleBeforeLoad(): Promise<void> {
  if (!isPlatformAuthenticated()) {
    const ok = await refreshPlatformAccessToken();
    if (!ok) {
      throw redirect({ to: '/platform/login' });
    }
  }
}

export const platformLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'platform-console', // layout route — mounted at root, no path
  beforeLoad: platformConsoleBeforeLoad,
  component: PlatformLayoutComponent,
});

function PlatformLayoutComponent(): JSX.Element {
  return (
    <PlatformShell>
      <Outlet />
    </PlatformShell>
  );
}

export function PlatformShell({ children }: { children: ReactNode }): JSX.Element {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Platform — Operator Console';
  }, []);

  async function handleLogout(): Promise<void> {
    // Await so the token is cleared BEFORE we navigate — otherwise the
    // /platform/login guard could see a still-present token and bounce back.
    await platformLogout();
    await navigate({ to: '/platform/login' });
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 flex">
      <aside className="w-60 bg-indigo-deep text-white flex flex-col">
        <div className="px-5 py-4 border-b border-white/10">
          <div className="font-semibold leading-tight">Platform</div>
          <div className="text-xs text-white/60">Operator Console</div>
        </div>
        <PlatformNav />
        <div className="mt-auto px-5 py-4 border-t border-white/10">
          <button
            type="button"
            onClick={handleLogout}
            className="text-xs text-white/70 hover:text-white underline"
          >
            Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="border-b border-gray-200 px-8 py-4">
          <h1 className="text-lg font-semibold text-indigo-deep">Platform Operator Console</h1>
          <p className="text-xs text-gray-500">Cross-tenant administration &amp; billing operations</p>
        </header>
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}

interface NavItem {
  label: string;
  to: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Tenants', to: '/platform/tenants' },
  { label: 'Scheduled Jobs', to: '/platform/scheduled-jobs' },
  { label: 'Paddle Mappings', to: '/platform/paddle-mappings' },
];

function PlatformNav(): JSX.Element {
  const location = useLocation();
  return (
    <nav aria-label="Platform" className="py-2 flex flex-col gap-1 text-sm">
      {NAV_ITEMS.map((item) => {
        const active =
          location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
        return (
          <Link
            key={item.to}
            to={item.to}
            className={`mx-2 px-3 py-2 rounded ${
              active ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
            }`}
          >
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}
